import * as d3 from 'd3'
import md5 from 'md5'
import { Pattern } from './chart/bars'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { FormatOptions } from '../../formatter'
import { HOUR_MILLISECONDS } from '@/utils/time'

export function addPattern(
  { type, color, strokeWidth }: Pattern,
  defs: d3.Selection<d3.BaseType, unknown, null, undefined>
): string {
  const id = `${type}-${md5(color + strokeWidth?.toString())}`
  const patternDef = d3.select(`#${id}`)
  if (patternDef.empty()) {
    switch (type) {
      case 'diagonal-stripes': {
        defs
          .append('pattern')
          .attr('id', id)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('width', 6)
          .attr('height', 6)
          .append('path')
          .attr('d', 'M-1.5,1.5 l3,-3 M0,6 l6,-6 M5,7 l3,-3')
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth ?? 2)
        break
      }
      case 'horizontal-stripes': {
        const sw = strokeWidth ?? 3
        defs
          .append('pattern')
          .attr('id', id)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('width', 3)
          .attr('height', 24 / 3)
          .append('path')
          .attr('d', `M-10,${sw / 2} l13,${sw / 2}`)
          .attr('stroke', color)
          .attr('stroke-width', sw)
      }
    }
  }
  return id
}

export const bucketSizeToFormat: Record<BucketSize, FormatOptions> = {
  MINUTE_10: {
    format: {
      type: 'intl-datetime',
      hour: 'numeric',
      minute: 'numeric'
    }
  },
  HOUR: {
    format: {
      type: 'intl-datetime',
      hour: 'numeric',
      minute: 'numeric'
    }
  },
  DAY: {
    format: {
      type: 'intl-datetime',
      month: 'numeric',
      day: 'numeric'
    }
  },
  WEEK: {
    format: { type: 'intl-datetime', week: 'numeric' },
    steps: [{ format: { type: 'locale-format-string', format: { sv: 'v%s', en: 'w%s' } } }]
  },
  MONTH: {
    format: {
      type: 'intl-datetime',
      month: 'short',
      year: 'numeric'
    }
  },
  QUARTER: {
    format: {
      type: 'intl-datetime',
      month: 'short',
      year: 'numeric'
    }
  },
  YEAR: {
    format: {
      type: 'intl-datetime',
      year: 'numeric'
    }
  },
  MAX: {
    format: {
      type: 'intl-datetime',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }
  }
}

export const bucketSizeToTooltipFormat: Record<BucketSize, FormatOptions> = {
  MINUTE_10: {
    format: {
      type: 'format-function',
      format: (value: number | string, local: string) => {
        const fromDate = new Date(value)
        const toDate = new Date(fromDate.getTime() + 10 * 60 * 1000)

        return `${fromDate.toLocaleTimeString(local, {
          hour: '2-digit',
          minute: '2-digit'
        })} - ${toDate.toLocaleTimeString(local, { hour: '2-digit', minute: '2-digit' })}`
      }
    }
  },
  HOUR: {
    format: {
      type: 'format-function',
      format: (value: number | string, local: string) => {
        const fromDate = new Date(value)
        const toDate = new Date(fromDate.getTime() + HOUR_MILLISECONDS)

        return `${fromDate.toLocaleTimeString(local, {
          hour: '2-digit',
          minute: '2-digit'
        })} - ${toDate.toLocaleTimeString(local, { hour: '2-digit', minute: '2-digit' })}`
      }
    }
  },
  DAY: {
    format: {
      type: 'intl-datetime',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
  },
  WEEK: {
    format: { type: 'intl-datetime', week: 'numeric' },
    steps: [{ format: { type: 'locale-format-string', format: { sv: 'Vecka %s', en: 'Week %s' } } }]
  },
  MONTH: {
    format: {
      type: 'intl-datetime',
      month: 'long'
    }
  },
  QUARTER: {
    format: {
      type: 'intl-datetime',
      quarter: 'short',
      year: 'numeric'
    }
  },
  YEAR: {
    format: {
      type: 'intl-datetime',
      year: 'numeric'
    }
  },
  MAX: {
    format: {
      type: 'intl-datetime',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }
  }
}
